// #2260: keeps the current task's answer across the re-login round-trip after the session expired
// mid-exam. Login is a full-page navigation, so everything that only lives in learningStore would be
// lost. Multi-file answers are already in S3 (saved under the retained uuid); this covers the rest:
// multiple choice, short/descriptive text (incl. INSTRUCTION_* variants) and single-file code, whose
// server-side save needs the auth that just expired.
//
// Only the CURRENT task is kept: moving to another task replaces learningStore.task with the server
// copy (after the unsubmitted-change warning), so the store never holds another task's unsubmitted answer.
//
// Lifecycle: written while the session is expired (and on the [로그인] click), restored when the
// taking page loads that task again for the SAME user uuid, deleted after that task is submitted
// successfully or the assessment is completed.

import { useLearningStore } from '~/store/learning/learningStore';
import { useLearningAssessmentStore } from '~/store/learning/assessment/learningAssessmentStore';
import { useUserStore } from '~/store/user/userStore';
import { TaskType } from '~~/package/mapper/learning/model/TaskType';

const KEY_PREFIX = 'codepresso:exam-answer-draft';

type DraftAnswer =
    | { kind: 'choice'; answerList: number[] }
    | { kind: 'descriptive'; text: string }
    | { kind: 'instructionDescriptive'; text: string }
    | { kind: 'code'; code: string };

export interface ExamAnswerDraft {
    userUuid: string;
    assessmentId: number;
    taskId: number;
    taskType: TaskType;
    answer: DraftAnswer;
    savedAt: number;
}

const draftKey = (userUuid: string, assessmentId: number, taskId: number): string =>
    `${KEY_PREFIX}:${userUuid}:${assessmentId}:${taskId}`;

const CHOICE_TYPES: TaskType[] = [TaskType.MULTIPLE_CHOICE, TaskType.INSTRUCTION_MULTIPLE_CHOICE];
const DESCRIPTIVE_TYPES: TaskType[] = [TaskType.SHORT_QA, TaskType.DESCRIPTIVE_QA];
const INSTRUCTION_DESCRIPTIVE_TYPES: TaskType[] = [TaskType.INSTRUCTION_SHORT_QA, TaskType.INSTRUCTION_DESCRIPTIVE_QA];
const CODE_TYPES: TaskType[] = [TaskType.CODE, TaskType.CODE_WITHOUT_RUN];

/** The current task's answer as it sits in the store, or null when this task type has no store-only answer. */
const currentAnswer = (): DraftAnswer | null => {
    const learningStore = useLearningStore();
    const type = learningStore.task.type as TaskType;
    if (CHOICE_TYPES.includes(type)) {
        return { kind: 'choice', answerList: [...(learningStore.task.multipleChoiceQuestion.multipleChoiceResult.answerList ?? [])] };
    }
    if (DESCRIPTIVE_TYPES.includes(type)) return { kind: 'descriptive', text: learningStore.descriptiveAnswer };
    if (INSTRUCTION_DESCRIPTIVE_TYPES.includes(type)) return { kind: 'instructionDescriptive', text: learningStore.linuxDescriptiveAnswer };
    if (CODE_TYPES.includes(type)) return { kind: 'code', code: learningStore.currentCode };
    return null;
};

/** Identity of the task on screen; null when there is no owner (empty uuid) or no task yet. */
const currentScope = (): { userUuid: string; assessmentId: number; taskId: number } | null => {
    const userUuid = useUserStore().userV2.uuid;
    const assessmentId = Number(useLearningAssessmentStore().proSkillAssure.assessmentId);
    const taskId = Number(useLearningStore().task.id);
    if (!userUuid || !assessmentId || !taskId) return null;
    return { userUuid, assessmentId, taskId };
};

const readDraft = (key: string): ExamAnswerDraft | null => {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    try {
        return JSON.parse(raw) as ExamAnswerDraft;
    } catch {
        localStorage.removeItem(key);
        return null;
    }
};

/** Serialized current answer — a cheap watch source for "the answer changed". */
export const examAnswerSignature = (): string => JSON.stringify(currentAnswer());

/** True when a draft is stored for the task on screen (it then has to keep up with later edits). */
export const hasExamAnswerDraft = (): boolean => {
    const scope = currentScope();
    return scope !== null && localStorage.getItem(draftKey(scope.userUuid, scope.assessmentId, scope.taskId)) !== null;
};

/**
 * Writes the current task's answer. No-op for task types whose answer is not store-only
 * (multi-file → S3, embed/linux → remote environment). Throws when storage is unavailable or full,
 * so the [로그인] click can refuse to leave the page.
 */
export const saveExamAnswerDraft = (): void => {
    const scope = currentScope();
    const answer = currentAnswer();
    if (!scope || !answer) return;
    const draft: ExamAnswerDraft = {
        ...scope,
        taskType: useLearningStore().task.type as TaskType,
        answer,
        savedAt: Date.now(),
    };
    localStorage.setItem(draftKey(scope.userUuid, scope.assessmentId, scope.taskId), JSON.stringify(draft));
};

/**
 * Applies the stored draft to the freshly loaded task (call right after updateLearningData) and
 * marks it as changed-but-not-submitted. Returns whether a draft was applied. A draft only matches
 * the same user uuid, assessment, task and task type.
 */
export const restoreExamAnswerDraft = (): boolean => {
    const scope = currentScope();
    if (!scope) return false;
    const draft = readDraft(draftKey(scope.userUuid, scope.assessmentId, scope.taskId));
    const learningStore = useLearningStore();
    if (!draft || draft.userUuid !== scope.userUuid || draft.taskType !== learningStore.task.type) return false;

    const { answer } = draft;
    switch (answer.kind) {
        case 'choice':
            learningStore.task.multipleChoiceQuestion.multipleChoiceResult.answerList = [...answer.answerList];
            break;
        case 'descriptive':
            // task.submitCode too: the answer components initialise from it when they mount.
            learningStore.task.submitCode = answer.text;
            learningStore.descriptiveAnswer = answer.text;
            break;
        case 'instructionDescriptive':
            learningStore.task.submitCode = answer.text;
            learningStore.linuxDescriptiveAnswer = answer.text;
            break;
        case 'code':
            // The editor renders task.submitCode; bump the key so an already-mounted editor re-reads it.
            learningStore.task.submitCode = answer.code;
            learningStore.currentCode = answer.code;
            learningStore.isUpdateCode = true;
            learningStore.editorKey += 1;
            break;
    }
    learningStore.isChangedButNotSubmit = true;
    return true;
};

export const removeExamAnswerDraft = (assessmentId: number, taskId: number): void => {
    const userUuid = useUserStore().userV2.uuid;
    if (!userUuid) return;
    localStorage.removeItem(draftKey(userUuid, assessmentId, taskId));
};

/** Assessment completed: drop every draft of this user for it. */
export const clearExamAnswerDrafts = (assessmentId: number): void => {
    const userUuid = useUserStore().userV2.uuid;
    if (!userUuid) return;
    const prefix = `${KEY_PREFIX}:${userUuid}:${assessmentId}:`;
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix)) keys.push(key);
    }
    keys.forEach(key => localStorage.removeItem(key));
};
